import React, { ChangeEvent, FC, PointerEvent, useMemo, useRef, useState } from 'react';
import { BasicButton, GhostButton, PrimaryButton } from 'src/components/widgets/buttons';
import { QuestionDialog, useDialogManager } from 'src/components/widgets/dialog';
import { T, uploadFile } from 'src/shared/helpers';
import styled from 'styled-components';
import { ProductArea, ProductSide, ReplaceAgentSideImageInput, UpsertAgentPrintAreaInput } from './types';

const MAX_IMAGE_BYTES = 12 * 1024 * 1024;

type Point = { x: number; y: number };
type AreaEditInput = Omit<UpsertAgentPrintAreaInput, 'expectedRevisionNumber'>;
type ImageEditInput = Omit<ReplaceAgentSideImageInput, 'expectedRevisionNumber'>;

interface AgentProductSidePreviewProps {
    variantRef: string;
    side: ProductSide;
    disabled?: boolean;
    onUpsertPrintArea: (input: AreaEditInput) => Promise<boolean>;
    onReplaceSideImage: (input: ImageEditInput) => Promise<boolean>;
}

const Card = styled.div`
    padding: 10px;
    border: 1px solid #eceff1;
    border-radius: 10px;
    background: #fafbfc;
`;

const Heading = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
    color: #37474f;
    font-size: 12px;
    font-weight: 600;
`;

const Reference = styled.span`
    color: #90a4ae;
    font-family: monospace;
    font-size: 9px;
    font-weight: 400;
`;

const Stage = styled.div<{ $editing: boolean }>`
    position: relative;
    display: flex;
    min-height: 130px;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: ${(props) => (props.$editing ? '1px solid #00a1b5' : '1px solid transparent')};
    border-radius: 8px;
    background: #eef2f4;
    user-select: none;
`;

const ImageFrame = styled.div<{ $aspectRatio: number; $editing: boolean }>`
    position: relative;
    width: min(100%, ${(props) => 260 * props.$aspectRatio}px);
    max-height: 260px;
    aspect-ratio: ${(props) => props.$aspectRatio};
    cursor: ${(props) => (props.$editing ? 'crosshair' : 'default')};
    touch-action: none;

    > img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        pointer-events: none;
    }
`;

const MissingPhoto = styled.span`
    padding: 24px;
    color: #90a4ae;
    font-size: 12px;
`;

const Rectangle = styled.div<{ $from: Point; $to: Point; $active?: boolean }>`
    position: absolute;
    left: ${(props) => Math.min(props.$from.x, props.$to.x) * 100}%;
    top: ${(props) => Math.min(props.$from.y, props.$to.y) * 100}%;
    width: ${(props) => Math.abs(props.$to.x - props.$from.x) * 100}%;
    height: ${(props) => Math.abs(props.$to.y - props.$from.y) * 100}%;
    border: 2px solid ${(props) => (props.$active ? '#e65100' : '#00a1b5')};
    background: ${(props) => (props.$active ? 'rgba(230, 81, 0, 0.12)' : 'rgba(0, 161, 181, 0.1)')};
    pointer-events: none;
`;

const AreaLabel = styled.span`
    position: absolute;
    left: 0;
    top: 0;
    max-width: 100%;
    padding: 2px 4px;
    transform: translateY(-100%);
    background: #00a1b5;
    color: white;
    font-size: 9px;
    white-space: nowrap;
`;

const Statuses = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 8px;
`;

const Status = styled.span<{ $missing?: boolean }>`
    padding: 3px 6px;
    border-radius: 999px;
    background: ${(props) => (props.$missing ? '#fff3e0' : '#e8f5e9')};
    color: ${(props) => (props.$missing ? '#e65100' : '#2e7d32')};
    font-size: 9px;
    font-weight: 600;
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
`;

const AreaSelect = styled.select`
    min-width: 120px;
    height: 32px;
    padding: 0 8px;
    border: 1px solid #cfd8dc;
    border-radius: 6px;
    background: white;
    color: #37474f;
    font-size: 11px;
`;

const Hint = styled.p`
    margin: 8px 0 0;
    color: #607d8b;
    font-size: 11px;
    line-height: 1.4;
`;

const ErrorText = styled.p`
    margin: 8px 0 0;
    color: #c62828;
    font-size: 11px;
`;

const HiddenFileInput = styled.input`
    display: none;
`;

const clamp = (value: number) => Math.min(Math.max(value, 0), 1);

const areaPoints = (area: ProductArea, side: ProductSide) => {
    const raw = area.bounds?.raw;
    const width = side.image?.widthPx ?? 0;
    const height = side.image?.heightPx ?? 0;
    if (!raw || width <= 0 || height <= 0 || raw.width <= 0 || raw.height <= 0) return null;

    return {
        from: { x: clamp((raw.x + width / 2) / width), y: clamp((raw.y + height / 2) / height) },
        to: { x: clamp((raw.x + width / 2 + raw.width) / width), y: clamp((raw.y + height / 2 + raw.height) / height) }
    };
};

const readImageDimensions = (file: File) =>
    new Promise<{ widthPx: number; heightPx: number }>((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve({ widthPx: image.naturalWidth, heightPx: image.naturalHeight });
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error(T._('The selected image could not be read.', 'Admin')));
        };
        image.src = objectUrl;
    });

export const AgentProductSidePreview: FC<AgentProductSidePreviewProps> = ({ variantRef, side, disabled, onUpsertPrintArea, onReplaceSideImage }) => {
    const stageRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { showDialog, closeDialog } = useDialogManager();
    const areas = side.areas ?? [];
    const [selectedAreaIndex, setSelectedAreaIndex] = useState(0);
    const selectedArea = areas[selectedAreaIndex] ?? areas[0] ?? null;
    const [editingArea, setEditingArea] = useState<ProductArea | null | undefined>(undefined);
    const initialPoints = useMemo(() => (editingArea ? areaPoints(editingArea, side) : null), [editingArea, side]);
    const [from, setFrom] = useState<Point | null>(null);
    const [to, setTo] = useState<Point | null>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);
    const image = side.image;
    const imageAspectRatio = image?.widthPx && image.heightPx ? image.widthPx / image.heightPx : 1;
    const scale = side.resolution?.pixelsPerCm;
    const isEditing = editingArea !== undefined;
    const activeFrom = from ?? initialPoints?.from ?? null;
    const activeTo = to ?? initialPoints?.to ?? null;

    const eventPoint = (event: PointerEvent<HTMLDivElement>): Point => {
        const box = stageRef.current?.getBoundingClientRect();
        if (!box) return { x: 0, y: 0 };
        return {
            x: clamp((event.clientX - box.left) / box.width),
            y: clamp((event.clientY - box.top) / box.height)
        };
    };

    const startEditing = (area: ProductArea | null) => {
        setEditingArea(area);
        setFrom(null);
        setTo(null);
        setLocalError(null);
    };

    const stopEditing = () => {
        setEditingArea(undefined);
        setFrom(null);
        setTo(null);
        setIsDrawing(false);
    };

    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        if (!isEditing || disabled || !image?.widthPx || !image.heightPx) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        const point = eventPoint(event);
        setFrom(point);
        setTo(point);
        setIsDrawing(true);
    };

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        if (isDrawing) setTo(eventPoint(event));
    };

    const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
        if (!isDrawing) return;
        event.currentTarget.releasePointerCapture(event.pointerId);
        setIsDrawing(false);
    };

    const saveArea = async () => {
        if (!activeFrom || !activeTo || !image?.widthPx || !image.heightPx || !side.entityReferenceKey) return;
        const width = Math.abs(activeTo.x - activeFrom.x);
        const height = Math.abs(activeTo.y - activeFrom.y);
        if (width <= 0.01 || height <= 0.01) {
            setLocalError(T._('Draw a larger print area before saving.', 'Admin'));
            return;
        }

        const saved = await onUpsertPrintArea({
            variantRef,
            sideRef: side.entityReferenceKey,
            areaRef: editingArea?.entityReferenceKey ?? null,
            name: null,
            x1: Math.min(activeFrom.x, activeTo.x) * image.widthPx,
            y1: Math.min(activeFrom.y, activeTo.y) * image.heightPx,
            x2: Math.max(activeFrom.x, activeTo.x) * image.widthPx,
            y2: Math.max(activeFrom.y, activeTo.y) * image.heightPx
        });
        if (saved) stopEditing();
    };

    const replaceImage = async (file: File) => {
        closeDialog('agent-replace-side-image');
        setLocalError(null);
        setIsUploading(true);
        try {
            const dimensions = await readImageDimensions(file);
            const url = await uploadFile(file);
            if (!side.entityReferenceKey) throw new Error(T._('This side has no reference key and cannot be edited.', 'Admin'));
            const saved = await onReplaceSideImage({
                variantRef,
                sideRef: side.entityReferenceKey,
                url,
                ...dimensions
            });
            if (saved) stopEditing();
        } catch (replaceError) {
            setLocalError((replaceError as Error).message || T._('The image could not be replaced.', 'Admin'));
        } finally {
            setIsUploading(false);
        }
    };

    const onFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setLocalError(T._('Select a valid image file.', 'Admin'));
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setLocalError(T._('Images must be smaller than 12 MB.', 'Admin'));
            return;
        }

        const hasGeometry = Boolean(scale || areas.length > 0);
        if (!hasGeometry) {
            void replaceImage(file);
            return;
        }

        showDialog(
            'agent-replace-side-image',
            <QuestionDialog
                title={T._('Replace side image', 'Admin')}
                message={T._(
                    'Print areas and scale are based on the current image. Replacing it will clear them for this side, so they can be configured again.',
                    'Admin'
                )}
                buttonYesLabel={T._('Replace image', 'Admin')}
                buttonNoLabel={T._('Cancel', 'Admin')}
                onYesClick={() => void replaceImage(file)}
                onNoClick={() => closeDialog('agent-replace-side-image')}
            />
        );
    };

    const canEdit = Boolean(variantRef && side.entityReferenceKey && image?.url && image.widthPx && image.heightPx);
    const canSave = Boolean(activeFrom && activeTo && !disabled);

    return (
        <Card>
            <Heading>
                <span>{side.name || T._('Unnamed side', 'Admin')}</span>
                {side.entityReferenceKey && <Reference>{side.entityReferenceKey}</Reference>}
            </Heading>
            <Stage $editing={isEditing}>
                {image?.url ? (
                    <ImageFrame
                        ref={stageRef}
                        $aspectRatio={imageAspectRatio}
                        $editing={isEditing}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                    >
                        <img src={image.url} alt={side.name || T._('Product side', 'Admin')} loading='lazy' draggable={false} />
                        {!isEditing &&
                            areas.map((area, index) => {
                                const points = areaPoints(area, side);
                                if (!points) return null;
                                const size =
                                    area.bounds?.width && area.bounds?.height
                                        ? `${area.bounds.width.toFixed(2)} × ${area.bounds.height.toFixed(2)} cm`
                                        : area.label;
                                return (
                                    <Rectangle key={area.entityReferenceKey ?? index} $from={points.from} $to={points.to}>
                                        {size && <AreaLabel>{size}</AreaLabel>}
                                    </Rectangle>
                                );
                            })}
                        {isEditing && activeFrom && activeTo && <Rectangle $from={activeFrom} $to={activeTo} $active />}
                    </ImageFrame>
                ) : (
                    <MissingPhoto>{T._('No image yet', 'Admin')}</MissingPhoto>
                )}
            </Stage>
            <Statuses>
                <Status $missing={!image?.url}>{image?.url ? T._('Image ready', 'Admin') : T._('Image missing', 'Admin')}</Status>
                <Status $missing={!scale}>{scale ? `${scale.toFixed(2)} px/cm` : T._('Scale missing', 'Admin')}</Status>
                <Status $missing={areas.length === 0}>{areas.length > 0 ? T._('Print area ready', 'Admin') : T._('Print area missing', 'Admin')}</Status>
            </Statuses>
            {isEditing ? (
                <>
                    <Hint>{T._('Press and drag on the image to redraw the print area, then save it.', 'Admin')}</Hint>
                    <Actions>
                        <PrimaryButton type='button' $size='small' disabled={!canSave} onClick={() => void saveArea()}>
                            {T._('Save area', 'Admin')}
                        </PrimaryButton>
                        <GhostButton type='button' $size='small' disabled={disabled} onClick={stopEditing}>
                            {T._('Cancel', 'Admin')}
                        </GhostButton>
                    </Actions>
                </>
            ) : (
                <Actions>
                    {areas.length > 0 && (
                        <>
                            {areas.length > 1 && (
                                <AreaSelect
                                    value={Math.min(selectedAreaIndex, areas.length - 1)}
                                    disabled={disabled}
                                    onChange={(event) => setSelectedAreaIndex(Number(event.target.value))}
                                >
                                    {areas.map((area, index) => (
                                        <option key={area.entityReferenceKey ?? index} value={index}>
                                            {area.label || `${T._('Print area', 'Admin')} ${index + 1}`}
                                        </option>
                                    ))}
                                </AreaSelect>
                            )}
                            <BasicButton type='button' $size='small' disabled={disabled || !canEdit} onClick={() => startEditing(selectedArea)}>
                                {T._('Edit print area', 'Admin')}
                            </BasicButton>
                        </>
                    )}
                    <BasicButton type='button' $size='small' disabled={disabled || !canEdit} onClick={() => startEditing(null)}>
                        {T._('Add print area', 'Admin')}
                    </BasicButton>
                    <BasicButton
                        type='button'
                        $size='small'
                        disabled={disabled || isUploading || !side.entityReferenceKey}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {isUploading ? T._('Uploading…', 'Admin') : T._('Replace image', 'Admin')}
                    </BasicButton>
                    <HiddenFileInput ref={fileInputRef} type='file' accept='image/*' onChange={onFileSelected} />
                </Actions>
            )}
            {!canEdit && image?.url && <Hint>{T._('Image dimensions are required before print areas can be edited.', 'Admin')}</Hint>}
            {localError && <ErrorText>{localError}</ErrorText>}
        </Card>
    );
};
